/**
 * @description
 * Analytics settings component for marketplace configuration.
 * Manages analytics tracking settings for marketplace operations.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState } from 'react';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';
import { AnalyticsSettingsProps } from './types';

export function AnalyticsSettings({ onRefresh }: AnalyticsSettingsProps) {
  const { config, updateConfig } = useMarketplaceConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);
      const data = {
        analyticsEnabled: formData.get('analyticsEnabled') === 'on',
        analyticsEndpoint: formData.get('analyticsEndpoint') as string || undefined,
        analyticsApiKey: formData.get('analyticsApiKey') as string || undefined,
      };

      await updateConfig(data);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update analytics settings');
    } finally {
      setLoading(false);
    }
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Configuration</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Configure analytics tracking for marketplace operations and app deployments.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="analyticsEnabled"
            name="analyticsEnabled"
            defaultChecked={config.analyticsEnabled}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="analyticsEnabled" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            Enable Analytics Tracking
          </label>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Track marketplace operations, app installations, and deployment metrics
        </p>

        <div>
          <label htmlFor="analyticsEndpoint" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Analytics Endpoint URL
          </label>
          <input
            type="url"
            id="analyticsEndpoint"
            name="analyticsEndpoint"
            defaultValue={config.analyticsEndpoint || ''}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="https://analytics.example.com/api/events"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            URL endpoint for sending analytics events
          </p>
        </div>

        <div>
          <label htmlFor="analyticsApiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Analytics API Key
          </label>
          <input
            type="password"
            id="analyticsApiKey"
            name="analyticsApiKey"
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="your-analytics-api-key"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            API key for authenticating with the analytics service. Leave empty to keep current key.
          </p>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md">
          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Analytics Events</h3>
          <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
            The following events are tracked when analytics is enabled:
          </p>
          <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
            <li>App installation started/completed</li>
            <li>App uninstallation</li>
            <li>Deployment success/failure</li>
            <li>User role assignments</li>
            <li>App enable/disable actions</li>
          </ul>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Analytics Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
